import { defineStore } from 'pinia'
import { date } from 'quasar'
import AiChatService from '@/services/ai/chat/AiChatService'
import AiChatHistoryService from '@/services/ai/chat/AiChatHistoryService'
import { buildViewScopedContext } from '@/services/ai/context/aiContextBuilder'
import { executeTool, getTool } from '@/services/ai/tools/ToolRegistry'
import { useUserStore } from '@/stores/userStore'
import { v4 as uuidv4 } from 'uuid'

const AI_PERSONAS = Object.freeze({
  dobby: {
    id: 'dobby',
    displayName: 'Dobby',
    avatar: '/images/dobby/dobby_avatar.jpg',
    avatarSide: '/images/dobby/dobby_avatar_side.jpg'
  },
  marvin: {
    id: 'marvin',
    displayName: 'Marvin',
    avatar: '/images/marvin/marvin-avatar.jpg',
    avatarSide: '/images/marvin/marvin-avatar_side.jpg'
  }
})
const PERSONA_STORAGE_KEY = 'aiChat.persona.v1'
const LAST_SESSION_STORAGE_KEY = 'aiChat.lastSessionId.v1'
const DEFAULT_PERSONA_ID = 'dobby'

const MAX_CLARIFICATION_CHOICES = 5
const SELECTION_LOCK_TTL_MS = 20 * 60 * 1000
const PENDING_ACTION_DEDUPE_WINDOW_MS = 60 * 1000
const LOCKABLE_ENTITY_TYPES = new Set(['monster', 'spell', 'class', 'note', 'vault', 'character'])

function normalizeClarificationChoice(choice = {}) {
  const label = String(choice?.label || choice?.name || choice?.title || choice?.value || '').trim()
  const value = String(choice?.value || choice?.name || choice?.title || choice?.label || '').trim()
  const displayLabel = label || value
  const displayValue = value || label
  if (!displayLabel || !displayValue) return null
  return {
    label: displayLabel,
    value: displayValue,
    id: String(choice?.id || choice?.index || '').trim() || null,
    entityType: String(choice?.entityType || '').trim().toLowerCase() || null,
    vaultName: String(choice?.vaultName || '').trim() || null,
    sourceId: String(choice?.sourceId || '').trim() || null,
    vaultId: String(choice?.vaultId || '').trim() || null,
    campaignId: String(choice?.campaignId || '').trim() || null
  }
}

function normalizeClarificationPayload(payload = null) {
  if (!payload || typeof payload !== 'object') return null
  const prompt = String(payload?.prompt || '').trim()
  const kind = String(payload?.kind || '').trim().toLowerCase() || 'clarification'
  const choices = Array.isArray(payload?.choices)
    ? payload.choices.map(normalizeClarificationChoice).filter(Boolean).slice(0, MAX_CLARIFICATION_CHOICES)
    : []
  const clarificationId = String(payload?.clarificationId || payload?.id || '').trim() || null
  const sourceTool = String(payload?.sourceTool || '').trim() || null
  const expectedAction = String(payload?.expectedAction || '').trim().toLowerCase() || null
  const expectedIntent = String(payload?.expectedIntent || '').trim().toLowerCase() || null
  const entityType = String(payload?.entityType || '').trim().toLowerCase() || null
  if (!prompt && !choices.length) return null
  return {
    clarificationId,
    kind,
    prompt: prompt || null,
    choices,
    sourceTool,
    expectedAction,
    expectedIntent,
    entityType
  }
}

function buildSimpleClarificationChoices(values = [], entityType = null) {
  const seen = new Set()
  const choices = []
  for (const value of Array.isArray(values) ? values : []) {
    const text = String(value || '').trim()
    if (!text) continue
    const key = text.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    choices.push({
      label: text,
      value: text,
      entityType: entityType || null
    })
    if (choices.length >= MAX_CLARIFICATION_CHOICES) break
  }
  return choices
}

function firstNonEmptyString(...values) {
  for (const value of values) {
    const text = String(value || '').trim()
    if (text) return text
  }
  return ''
}

function normalizeClarificationState(state = null) {
  if (!state || typeof state !== 'object') return null
  const payload = normalizeClarificationPayload(state)
  if (!payload) return null
  const createdAt = firstNonEmptyString(state.createdAt, state.at)
  return {
    ...payload,
    createdAt: createdAt || new Date().toISOString()
  }
}

function normalizeClarificationResolution(value = null) {
  if (!value || typeof value !== 'object') return null
  const clarificationId = String(value?.clarificationId || value?.id || '').trim() || null
  const choiceId = String(value?.choiceId || '').trim() || null
  const choiceValue = String(value?.choiceValue || value?.value || '').trim() || null
  const choiceLabel = String(value?.choiceLabel || value?.label || '').trim() || null
  if (!clarificationId && !choiceId && !choiceValue && !choiceLabel) return null
  return {
    clarificationId,
    choiceId,
    choiceValue,
    choiceLabel
  }
}

function normalizeSelectionLock(lock = null) {
  if (!lock || typeof lock !== 'object') return null
  const entityType = String(lock.entityType || '').trim().toLowerCase()
  const entityName = firstNonEmptyString(lock.entityName)
  const lookupQueryHint = firstNonEmptyString(lock.lookupQueryHint, entityName)
  const entityId = firstNonEmptyString(lock.entityId)
  const lockedAt = firstNonEmptyString(lock.lockedAt)

  if (!LOCKABLE_ENTITY_TYPES.has(entityType) || !lookupQueryHint) return null
  return {
    entityType,
    entityId: entityId || null,
    entityName: entityName || null,
    lookupQueryHint,
    lockedAt: lockedAt || new Date().toISOString()
  }
}

function parseSelectionLockTimestamp(value = '') {
  const stamp = String(value || '').trim()
  if (!stamp) return 0
  const parsed = Date.parse(stamp)
  return Number.isFinite(parsed) ? parsed : 0
}

function isSelectionLockExpired(lock = null, now = Date.now()) {
  const normalized = normalizeSelectionLock(lock)
  if (!normalized) return true
  const lockedAtMs = parseSelectionLockTimestamp(normalized.lockedAt)
  if (!lockedAtMs) return false
  return now - lockedAtMs > SELECTION_LOCK_TTL_MS
}

function extractExplicitSelectionCommand(text = '') {
  const raw = String(text || '').trim()
  if (!raw) return null
  const match = raw.match(
    /\buse\s+(monster|spell|class|note|vault|character)\s+"?([^"\n]+?)"?(?:[.!?]|$)/i
  )
  if (!match) return null
  const entityType = String(match[1] || '').trim().toLowerCase()
  const entityName = String(match[2] || '').trim()
  if (!LOCKABLE_ENTITY_TYPES.has(entityType) || !entityName) return null
  return {
    entityType,
    entityName
  }
}

function shouldClearSelectionLock(text = '') {
  return /\b(?:clear|forget|reset)\s+(?:selection|context|target)\b/i.test(String(text || ''))
}

function buildSelectionLockFromState(state = null) {
  if (!state || typeof state !== 'object') return null
  const entityType = String(state.resolvedEntityType || '').trim().toLowerCase()
  const entityName = firstNonEmptyString(state.resolvedEntityName)
  const lookupQueryHint = firstNonEmptyString(
    state.canonicalQueryHint,
    state.lookupQueryHint,
    state.focusQuery,
    entityName
  )
  const entityId = firstNonEmptyString(state.resolvedEntityId)
  if (!LOCKABLE_ENTITY_TYPES.has(entityType) || !lookupQueryHint) return null
  return normalizeSelectionLock({
    entityType,
    entityId,
    entityName,
    lookupQueryHint,
    lockedAt: new Date().toISOString()
  })
}

function deriveSelectionLock({ previousTopicState = null, nextTopicState = null, latestUserText = '' } = {}) {
  const now = Date.now()
  const previousLock = normalizeSelectionLock(previousTopicState?.selectionLock)
  const activePreviousLock =
    previousLock && !isSelectionLockExpired(previousLock, now) ? previousLock : null
  const explicitSelection = extractExplicitSelectionCommand(latestUserText)
  const normalizedIntent = String(nextTopicState?.intentHint || '').trim().toLowerCase()
  const nextLookupHint = firstNonEmptyString(
    nextTopicState?.canonicalQueryHint,
    nextTopicState?.lookupQueryHint,
    nextTopicState?.focusQuery
  ).toLowerCase()

  if (shouldClearSelectionLock(latestUserText)) return null

  if (explicitSelection) {
    const lockedFromState = buildSelectionLockFromState(nextTopicState)
    if (lockedFromState) return lockedFromState
    return normalizeSelectionLock({
      entityType: explicitSelection.entityType,
      entityName: explicitSelection.entityName,
      lookupQueryHint: explicitSelection.entityName,
      lockedAt: new Date().toISOString()
    })
  }

  const inferredLock = buildSelectionLockFromState(nextTopicState)
  if (inferredLock && ['rules_lookup', 'monster_image', 'personal_data_lookup'].includes(normalizedIntent)) {
    return inferredLock
  }

  if (!activePreviousLock) return null

  if (
    ['rules_lookup', 'monster_image', 'personal_data_lookup'].includes(normalizedIntent) &&
    nextLookupHint &&
    nextLookupHint !== String(activePreviousLock.lookupQueryHint || '').trim().toLowerCase()
  ) {
    return null
  }

  return activePreviousLock
}

function normalizeTopicState(state = null) {
  if (!state || typeof state !== 'object') return null
  const intentHint = firstNonEmptyString(state.intentHint)
  const queryKind = firstNonEmptyString(state.queryKind)
  const responseMode = firstNonEmptyString(state.responseMode)
  const lookupQueryHint = firstNonEmptyString(state.lookupQueryHint)
  const canonicalQueryHint = firstNonEmptyString(state.canonicalQueryHint)
  const focusQuery = firstNonEmptyString(state.focusQuery)
  const focusEntityType = firstNonEmptyString(state.focusEntityType)
  const resolvedEntityType = firstNonEmptyString(state.resolvedEntityType)
  const resolvedEntityId = firstNonEmptyString(state.resolvedEntityId)
  const resolvedEntityName = firstNonEmptyString(state.resolvedEntityName)
  const vaultName = firstNonEmptyString(state.vaultName)
  const personalDataTargetType = firstNonEmptyString(state.personalDataTargetType)
  const selectionLock = normalizeSelectionLock(state.selectionLock)
  const clarificationState = normalizeClarificationState(state.clarificationState)

  if (
    !intentHint &&
    !queryKind &&
    !responseMode &&
    !lookupQueryHint &&
    !canonicalQueryHint &&
    !focusQuery &&
    !focusEntityType &&
    !resolvedEntityType &&
    !resolvedEntityId &&
    !resolvedEntityName &&
    !vaultName &&
    !personalDataTargetType &&
    !selectionLock &&
    !clarificationState
  ) {
    return null
  }

  return {
    intentHint: intentHint || null,
    queryKind: queryKind || null,
    responseMode: responseMode || null,
    lookupQueryHint: lookupQueryHint || null,
    canonicalQueryHint: canonicalQueryHint || null,
    focusQuery: focusQuery || null,
    focusEntityType: focusEntityType || null,
    resolvedEntityType: resolvedEntityType || null,
    resolvedEntityId: resolvedEntityId || null,
    resolvedEntityName: resolvedEntityName || null,
    vaultName: vaultName || null,
    personalDataTargetType: personalDataTargetType || null,
    selectionLock,
    clarificationState
  }
}

function buildTopicStateFromJob(
  job = null,
  { previousTopicState = null, latestUserText = '' } = {}
) {
  const result = job?.result || {}
  const trace = result?.aidbTracePayload && typeof result.aidbTracePayload === 'object'
    ? result.aidbTracePayload
    : {}
  const classification = result?.classifierTelemetry?.classification &&
    typeof result.classifierTelemetry.classification === 'object'
    ? result.classifierTelemetry.classification
    : {}
  const entityOperation =
    classification?.entityOperation && typeof classification.entityOperation === 'object'
      ? classification.entityOperation
      : {}
  const responsePayload = trace?.responsePayload && typeof trace.responsePayload === 'object'
    ? trace.responsePayload
    : {}

  const lookupQueryHint = firstNonEmptyString(
    classification.canonicalQueryHint,
    classification.lookupQueryHint,
    classification.monsterQueryHint,
    classification.noteQueryHint,
    trace?.requestPayload?.latestUserMessage
  )
  const focusQuery = firstNonEmptyString(
    classification.canonicalQueryHint,
    classification.lookupQueryHint,
    classification.monsterQueryHint,
    classification.noteQueryHint,
    responsePayload.resolvedEntityName
  )
  const focusEntityType = firstNonEmptyString(
    trace.resolvedEntityType,
    classification.rulesEntityType,
    entityOperation.targetType
  )

  const nextState = normalizeTopicState({
    intentHint: firstNonEmptyString(classification.intentHint, trace.intentHint),
    queryKind: firstNonEmptyString(classification.queryKind, trace.queryKind),
    responseMode: firstNonEmptyString(classification.responseMode),
    lookupQueryHint,
    canonicalQueryHint: firstNonEmptyString(classification.canonicalQueryHint, lookupQueryHint),
    focusQuery,
    focusEntityType,
    resolvedEntityType: firstNonEmptyString(trace.resolvedEntityType),
    resolvedEntityId: firstNonEmptyString(trace.resolvedEntityId),
    resolvedEntityName: firstNonEmptyString(responsePayload.resolvedEntityName),
    vaultName: firstNonEmptyString(classification.vaultName),
    personalDataTargetType: firstNonEmptyString(
      entityOperation.targetType,
      classification.personalDataTargetType
    )
  })

  const selectionLock = deriveSelectionLock({
    previousTopicState,
    nextTopicState: nextState,
    latestUserText
  })
  const clarificationState = normalizeClarificationState(
    job?.result?.clarification || job?.stream?.clarification || null
  )

  return normalizeTopicState({
    ...(nextState || {}),
    selectionLock,
    clarificationState
  })
}

function formatMessageStamp(value = null) {
  if (!value) return ''
  const parsed = Date.parse(String(value))
  if (!Number.isFinite(parsed)) return ''
  return date.formatDate(parsed, 'HH:mm')
}

function normalizeHistoryMessage(message = {}) {
  const metadata = message?.metadata && typeof message.metadata === 'object' ? message.metadata : {}
  const metadataPersonaId = String(metadata?.personaId || '').trim()
  return {
    id: String(message?.messageId || '').trim() || null,
    role: String(message?.role || 'assistant').trim().toLowerCase(),
    content: String(message?.contentText || '').trim(),
    stamp: formatMessageStamp(message?.createdAt),
    personaId:
      metadataPersonaId ||
      (String(message?.role || '').trim().toLowerCase() === 'assistant'
        ? DEFAULT_PERSONA_ID
        : null),
    metadata,
    persisted: true
  }
}

function getAttachmentSummaryItems(metadata = null) {
  const items = Array.isArray(metadata?.attachments?.items) ? metadata.attachments.items : []
  return items
    .map((item) => ({
      name: String(item?.name || '').trim() || null,
      type: String(item?.type || '').trim() || null,
      size: Number(item?.size || 0)
    }))
    .filter((item) => item.name || item.type || item.size > 0)
}

function hasRenderableMessageBody(message = {}) {
  if (String(message?.content || '').trim()) return true
  return getAttachmentSummaryItems(message?.metadata).length > 0
}

function buildLocalAttachmentSummary(files = []) {
  const items = (Array.isArray(files) ? files : [])
    .map((file) => ({
      name: String(file?.name || '').trim() || null,
      type: String(file?.type || '').trim() || null,
      size: Number(file?.size || 0)
    }))
    .filter((file) => file.name || file.type || file.size > 0)

  return {
    count: items.length,
    items
  }
}

function normalizeSessionSummary(session = {}) {
  return {
    sessionId: String(session?.sessionId || '').trim() || null,
    title: String(session?.title || 'New chat').trim() || 'New chat',
    status: String(session?.status || 'active').trim() || 'active',
    personaId: String(session?.personaId || '').trim() || null,
    sessionState: session?.sessionState && typeof session.sessionState === 'object'
      ? session.sessionState
      : {},
    messageCount: Number(session?.messageCount || 0),
    lastMessagePreview: String(session?.lastMessagePreview || '').trim() || null,
    createdAt: String(session?.createdAt || '').trim() || null,
    updatedAt: String(session?.updatedAt || '').trim() || null
  }
}

export { AI_PERSONAS }

export const useAiChatStore = defineStore('aiChat', {
  state: () => ({
    isOpen: false,
    isSending: false,
    isTyping: false,
    messages: [],
    attachments: [],
    _confirmSeq: 1,
    _confirmResolvers: {},
    currentStreamCtl: null,
    aiAvatarVariant: 'front',
    aiStreamPhase: '',
    aiStatusText: '',
    _lastAiStatusKey: '',
    topicState: null,
    _pendingActionLedger: {},
    selectedPersonaId: DEFAULT_PERSONA_ID,
    activeSessionId: null,
    sessionList: [],
    sessionsCursor: null,
    messagesCursor: null,
    historyLoaded: false,
    historyLoading: false,
    isDeletingSession: false
  }),

  getters: {
    aiAvatarUrl: (state) => {
      const persona = AI_PERSONAS[state.selectedPersonaId] || AI_PERSONAS[DEFAULT_PERSONA_ID]
      return state.aiAvatarVariant === 'side' ? persona.avatarSide : persona.avatar
    },
    currentPersona: (state) => AI_PERSONAS[state.selectedPersonaId] || AI_PERSONAS[DEFAULT_PERSONA_ID],
    availablePersonas: () => Object.values(AI_PERSONAS),
    activeSessionSummary: (state) =>
      state.sessionList.find((session) => session.sessionId === state.activeSessionId) || null
  },

  actions: {
    loadPersonaFromStorage() {
      try {
        const stored = window?.localStorage?.getItem(PERSONA_STORAGE_KEY)
        if (stored && AI_PERSONAS[stored]) this.selectedPersonaId = stored
      } catch {
        /* ignore */
      }
    },

    setPersona(id) {
      if (!AI_PERSONAS[id]) return
      this.selectedPersonaId = id
      try {
        window?.localStorage?.setItem(PERSONA_STORAGE_KEY, id)
      } catch {
        /* ignore */
      }
      if (this.activeSessionId) {
        void this.syncActiveSessionState()
      }
    },

    loadTopicStateFromStorage() {
      this.topicState = normalizeTopicState(this.topicState)
    },

    persistTopicStateToStorage(state = null) {
      this.topicState = normalizeTopicState(state)
    },

    resetTopicState() {
      this.topicState = null
      this.persistTopicStateToStorage(null)
    },

    setTopicState(state = null) {
      const normalized = normalizeTopicState(state)
      this.topicState = normalized
      this.persistTopicStateToStorage(normalized)
    },

    loadLastSessionId() {
      if (typeof window === 'undefined' || !window.localStorage) return null
      try {
        return window.localStorage.getItem(LAST_SESSION_STORAGE_KEY) || null
      } catch {
        return null
      }
    },

    persistLastSessionId(sessionId = null) {
      if (typeof window === 'undefined' || !window.localStorage) return
      try {
        const value = String(sessionId || '').trim()
        if (!value) {
          window.localStorage.removeItem(LAST_SESSION_STORAGE_KEY)
          return
        }
        window.localStorage.setItem(LAST_SESSION_STORAGE_KEY, value)
      } catch {
        /* ignore storage failures */
      }
    },

    buildPersistedSessionState() {
      return {
        topicState: this.topicState || null
      }
    },

    applySessionSummary(session = null) {
      const normalized = normalizeSessionSummary(session)
      if (!normalized.sessionId) return null
      const existingIndex = this.sessionList.findIndex(
        (entry) => entry.sessionId === normalized.sessionId
      )
      const merged =
        existingIndex >= 0
          ? {
              ...this.sessionList[existingIndex],
              ...normalized
            }
          : normalized
      if (existingIndex >= 0) {
        this.sessionList.splice(existingIndex, 1)
      }
      this.sessionList.unshift(merged)
      this.sessionList = [...this.sessionList].sort((left, right) => {
        const rightTime = Date.parse(String(right?.updatedAt || right?.createdAt || '')) || 0
        const leftTime = Date.parse(String(left?.updatedAt || left?.createdAt || '')) || 0
        return rightTime - leftTime
      })
      return merged
    },

    applySessionMessages(messages = [], { append = false } = {}) {
      const normalized = (Array.isArray(messages) ? messages : [])
        .map((message) => normalizeHistoryMessage(message))
        .filter((message) => hasRenderableMessageBody(message))
      if (!append) {
        this.messages = normalized
        return
      }
      const existingIds = new Set(
        this.messages.map((message) => String(message?.id || '').trim()).filter(Boolean)
      )
      const next = normalized.filter((message) => !message.id || !existingIds.has(message.id))
      this.messages = [...next, ...this.messages]
    },

    async loadSessionList({ reset = false } = {}) {
      if (this.historyLoading) return
      this.historyLoading = true
      try {
        const result = await AiChatHistoryService.listSessions({
          limit: 20,
          cursor: reset ? null : this.sessionsCursor
        })
        const nextSessions = (result.sessions || []).map((session) => normalizeSessionSummary(session))
        if (reset) {
          this.sessionList = nextSessions
        } else {
          const byId = new Map(this.sessionList.map((session) => [session.sessionId, session]))
          for (const session of nextSessions) {
            byId.set(session.sessionId, { ...(byId.get(session.sessionId) || {}), ...session })
          }
          this.sessionList = Array.from(byId.values()).sort((left, right) => {
            const rightTime = Date.parse(String(right?.updatedAt || right?.createdAt || '')) || 0
            const leftTime = Date.parse(String(left?.updatedAt || left?.createdAt || '')) || 0
            return rightTime - leftTime
          })
        }
        this.sessionsCursor = result.nextCursor || null
      } finally {
        this.historyLoading = false
      }
    },

    async ensureHistoryLoaded() {
      if (this.historyLoaded) return
      await this.loadSessionList({ reset: true })
      this.historyLoaded = true

      const lastSessionId = this.loadLastSessionId()
      const targetSessionId =
        lastSessionId && this.sessionList.some((session) => session.sessionId === lastSessionId)
          ? lastSessionId
          : this.sessionList[0]?.sessionId || null
      if (targetSessionId) {
        await this.openSession(targetSessionId)
      } else {
        this.startNewChat()
      }
    },

    async openSession(sessionId, { beforeMessageId = null, append = false } = {}) {
      const normalizedSessionId = String(sessionId || '').trim()
      if (!normalizedSessionId) {
        this.startNewChat()
        return
      }
      this.historyLoading = true
      try {
        const result = await AiChatHistoryService.getSession({
          sessionId: normalizedSessionId,
          limit: 40,
          beforeMessageId
        })
        this.activeSessionId = normalizedSessionId
        this.persistLastSessionId(normalizedSessionId)
        this.applySessionSummary(result.session)
        this.applySessionMessages(result.messages, { append })
        this.messagesCursor = result.nextCursor || null
        const sessionTopicState = normalizeTopicState(result.session?.sessionState?.topicState || null)
        this.topicState = sessionTopicState
        const sessionPersonaId = String(result.session?.personaId || '').trim()
        if (sessionPersonaId && AI_PERSONAS[sessionPersonaId]) {
          this.selectedPersonaId = sessionPersonaId
        }
      } finally {
        this.historyLoading = false
      }
    },

    async loadOlderMessages() {
      if (!this.activeSessionId || !this.messagesCursor || this.historyLoading) return
      await this.openSession(this.activeSessionId, {
        beforeMessageId: this.messagesCursor,
        append: true
      })
    },

    async deleteSession(sessionId = this.activeSessionId) {
      const normalizedSessionId = String(sessionId || '').trim()
      if (!normalizedSessionId || this.isDeletingSession) return false
      this.isDeletingSession = true
      try {
        await AiChatHistoryService.deleteSession(normalizedSessionId)
        this.sessionList = this.sessionList.filter((session) => session.sessionId !== normalizedSessionId)
        if (this.activeSessionId === normalizedSessionId) {
          const fallbackSessionId = this.sessionList[0]?.sessionId || null
          if (fallbackSessionId) {
            await this.openSession(fallbackSessionId)
          } else {
            this.startNewChat()
          }
        }
        return true
      } finally {
        this.isDeletingSession = false
      }
    },

    startNewChat() {
      this.activeSessionId = null
      this.messages = []
      this.messagesCursor = null
      this.topicState = null
      this.attachments = []
      this.persistLastSessionId(null)
      this.currentStreamCtl = null
      this.isTyping = false
      this.isSending = false
      this.resetAiAvatarState()
    },

    async syncActiveSessionState() {
      if (!this.activeSessionId) return null
      try {
        return await AiChatHistoryService.setSessionState({
          sessionId: this.activeSessionId,
          sessionState: this.buildPersistedSessionState(),
          personaId: this.selectedPersonaId || DEFAULT_PERSONA_ID
        })
      } catch (error) {
        console.warn('[AiChat] syncActiveSessionState failed', error)
        return null
      }
    },

    buildPendingActionExecutionKey(action = {}, latestUserText = '') {
      const idempotencyKey = String(action?.idempotencyKey || '').trim()
      if (!idempotencyKey) return ''
      const userScope = String(latestUserText || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .slice(0, 240)
      return `${idempotencyKey}:${userScope}`
    },

    prunePendingActionLedger(now = Date.now()) {
      const next = {}
      for (const [key, timestamp] of Object.entries(this._pendingActionLedger || {})) {
        const time = Number(timestamp || 0)
        if (!Number.isFinite(time) || now - time > PENDING_ACTION_DEDUPE_WINDOW_MS) continue
        next[key] = time
      }
      this._pendingActionLedger = next
    },

    hasRecentPendingActionExecution(key = '', now = Date.now()) {
      const normalizedKey = String(key || '').trim()
      if (!normalizedKey) return false
      this.prunePendingActionLedger(now)
      const timestamp = Number(this._pendingActionLedger?.[normalizedKey] || 0)
      if (!Number.isFinite(timestamp)) return false
      return now - timestamp <= PENDING_ACTION_DEDUPE_WINDOW_MS
    },

    markPendingActionExecuted(key = '', now = Date.now()) {
      const normalizedKey = String(key || '').trim()
      if (!normalizedKey) return
      this.prunePendingActionLedger(now)
      this._pendingActionLedger = {
        ...(this._pendingActionLedger || {}),
        [normalizedKey]: now
      }
    },

    resetAiAvatarState() {
      this.aiAvatarVariant = 'front'
      this.aiStreamPhase = ''
      this.aiStatusText = ''
      this._lastAiStatusKey = ''
    },

    updateAiAvatarStatus({ phase = '', statusText = '' } = {}) {
      const nextPhase = typeof phase === 'string' ? phase.trim() : ''
      const nextStatusText = typeof statusText === 'string' ? statusText.trim() : ''
      if (!nextPhase && !nextStatusText) return

      const nextKey = `${nextPhase}::${nextStatusText}`
      this.aiStreamPhase = nextPhase
      this.aiStatusText = nextStatusText

      if (!nextKey || nextKey === this._lastAiStatusKey) return

      this.aiAvatarVariant = this.aiAvatarVariant === 'front' ? 'side' : 'front'
      this._lastAiStatusKey = nextKey
    },

    cancelCurrentStream() {
      try {
        if (this.currentStreamCtl && typeof this.currentStreamCtl.cancel === 'function') {
          this.currentStreamCtl.cancel()
        }
      } catch {
        /* ignore cancellation errors */
      }
      this.currentStreamCtl = null
      this.isTyping = false
      this.resetAiAvatarState()
    },

    async requestConfirm({ title = 'Confirm change', message = 'Allow this action?' }) {
      this.isTyping = false
      const idxPh = this.messages.findIndex((m) => m.role === 'assistant' && m.placeholder)
      if (idxPh >= 0) this.messages.splice(idxPh, 1)

      const id = this._confirmSeq++
      this.messages.push({
        role: 'assistant',
        type: 'confirm',
        confirmId: id,
        title,
        content: message,
        stamp: date.formatDate(Date.now(), 'HH:mm')
      })

      return await new Promise((resolve) => {
        this._confirmResolvers[id] = { resolve }
      })
    },

    resolveConfirm(id, ok) {
      const entry = this._confirmResolvers[id]
      if (entry && typeof entry.resolve === 'function') {
        entry.resolve(!!ok)
      }
      delete this._confirmResolvers[id]

      const idx = this.messages.findIndex((m) => m.type === 'confirm' && m.confirmId === id)
      if (idx >= 0) {
        this.messages.splice(idx, 1, {
          role: 'assistant',
          content: ok ? 'Approved' : 'Cancelled',
          stamp: date.formatDate(Date.now(), 'HH:mm')
        })
      }
    },

    setOpen(v) {
      this.isOpen = !!v
      if (this.isOpen) {
        void this.ensureHistoryLoaded()
      }
    },

    toggleOpen() {
      this.setOpen(!this.isOpen)
    },

    pushMessage(role, content) {
      const msg = { role, content, stamp: date.formatDate(Date.now(), 'HH:mm') }
      if (role === 'assistant') msg.personaId = this.selectedPersonaId || DEFAULT_PERSONA_ID
      this.messages.push(msg)
    },

    addAttachment(fileObj) {
      this.attachments.push(fileObj)
    },

    removeAttachment(idx) {
      this.attachments.splice(idx, 1)
    },

    async sendUserMessage(text, { modelOverride = null, clarificationResolution = null } = {}) {
      const content = String(text || '').trim()
      const normalizedClarificationResolution = normalizeClarificationResolution(clarificationResolution)
      const filesForUpload = (this.attachments || []).map((attachment) => ({
        data: attachment.data,
        type: attachment.type,
        name: attachment.name,
        size: attachment.size
      }))
      if (!content && !filesForUpload.length && !normalizedClarificationResolution) return

      const attachmentSummary = buildLocalAttachmentSummary(filesForUpload)
      this.messages.push({
        role: 'user',
        content,
        stamp: date.formatDate(Date.now(), 'HH:mm'),
        metadata: attachmentSummary.count ? { attachments: attachmentSummary } : {}
      })
      this.isSending = true
      this.isTyping = true
      this.resetAiAvatarState()
      const effectiveSessionId = this.activeSessionId || uuidv4()
      const effectiveTurnId = uuidv4()

      let assistantIdx = null
      const ensureAssistant = () => {
        if (assistantIdx == null) {
          assistantIdx = this.messages.length
          this.messages.push({ role: 'assistant', content: '', stamp: '', placeholder: true, personaId: this.selectedPersonaId || DEFAULT_PERSONA_ID })
        }
        return assistantIdx
      }
      const setAssistantContent = (value) => {
        const nextText = String(value || '')
        if (!nextText.trim()) return
        const idx = ensureAssistant()
        const msg = this.messages[idx]
        msg.content = nextText
        msg.placeholder = false
        if (!msg.stamp) {
          msg.stamp = date.formatDate(Date.now(), 'HH:mm')
        }
      }
      const setAssistantClarification = (clarificationPayload) => {
        const idx = ensureAssistant()
        const msg = this.messages[idx]
        if (clarificationPayload && typeof clarificationPayload === 'object') {
          msg.clarification = clarificationPayload
        } else if (msg && Object.prototype.hasOwnProperty.call(msg, 'clarification')) {
          delete msg.clarification
        }
      }

      try {
        ensureAssistant()

        const selectionText = (() => {
          try {
            return (window.getSelection && window.getSelection().toString()) || ''
          } catch {
            return ''
          }
        })()

        const ctx = buildViewScopedContext({
          selectionText,
          topicState: this.topicState
        })
        // Pass selected persona to backend — resolveAgentPersona() reads contextSnapshot.persona
        ctx.persona = this.selectedPersonaId || DEFAULT_PERSONA_ID
        const userStore = useUserStore()
        try {
          const userName = userStore?.userName || ''
          const userId = userStore?.userId || ''
          if (userName || userId) {
            ctx.user = { name: userName || null, id: userId || null }
          }
        } catch {
          /* best-effort user context */
        }

        let streamTimer = null
        let latestStreamText = ''
        const flushStream = () => {
          if (streamTimer) {
            clearTimeout(streamTimer)
            streamTimer = null
          }
          if (latestStreamText) {
            setAssistantContent(latestStreamText)
          }
        }
        const setAssistantContentThrottled = (value) => {
          const nextText = String(value || '')
          if (!nextText.trim()) return
          latestStreamText = nextText
          if (streamTimer) return
          streamTimer = setTimeout(flushStream, 100)
        }

        const stripToolPatterns = (value) =>
          typeof value === 'string'
            ? value
                .replace(/<tool_call>[\s\S]*?<\/tool_call>/gi, '')
                .replace(/<\/?tool_call[^>]*>/gi, '')
                .replace(/\{\s*"name"\s*:\s*"[^"]+"\s*,\s*"arguments"\s*:\s*\{[^}]*\}\s*\}/g, '')
                .replace(/\n{3,}/g, '\n\n')
                .trim()
            : value || ''

        const formatPendingActionError = (actionName, result = {}) => {
          const errorCode = String(result?.error || '').trim()
          const availableVaults = Array.isArray(result?.availableVaults)
            ? result.availableVaults.filter(Boolean)
            : []
          const matchingVaults = Array.isArray(result?.matchingVaults)
            ? result.matchingVaults.filter(Boolean)
            : []
          const matchingNotes = Array.isArray(result?.matchingNotes)
            ? result.matchingNotes.filter(Boolean)
            : []
          const requestedVaultName = String(result?.requestedVaultName || '').trim()
          const requestedNoteName = String(result?.requestedNoteName || '').trim()

          if (actionName === 'update_note' || actionName === 'create_note') {
            if (actionName === 'create_note' && errorCode === 'note_content_too_vague') {
              return (
                String(result?.message || '').trim() ||
                'I still need the actual campaign idea or note details before I can create that note.'
              )
            }
            if (errorCode === 'vault_not_found' && availableVaults.length) {
              return requestedVaultName
                ? `I could not find a vault matching "${requestedVaultName}". Which vault do you want: ${availableVaults.slice(0, 5).join(', ')}?`
                : `I could not find that vault. Which vault do you want: ${availableVaults.slice(0, 5).join(', ')}?`
            }
            if (errorCode === 'vault_ambiguous' && matchingVaults.length) {
              return requestedVaultName
                ? `I found multiple vaults that could match "${requestedVaultName}". Which one do you want: ${matchingVaults.slice(0, 5).join(', ')}?`
                : `I found multiple matching vaults. Which one do you want: ${matchingVaults.slice(0, 5).join(', ')}?`
            }
          }

          if (actionName === 'update_note') {
            if (errorCode === 'note_candidate' && matchingNotes.length) {
              return `I found one likely note. Do you mean ${matchingNotes[0]}?`
            }
            if (errorCode === 'note_ambiguous' && matchingNotes.length) {
              return `I found multiple matching notes. Which one do you want: ${matchingNotes.slice(0, 5).join(', ')}?`
            }
            if (errorCode === 'note_not_found') {
              return requestedNoteName
                ? `I could not find a note matching "${requestedNoteName}". Tell me the exact note title, or the vault to search.`
                : `I could not find that note. Tell me the exact note title, or the vault to search.`
            }
          }

          return String(result?.error || 'Action failed')
        }
        const buildPendingActionClarification = (actionName, result = {}, prompt = '') => {
          const normalizedPrompt = String(prompt || '').trim()
          if (!normalizedPrompt) return null
          const errorCode = String(result?.error || '').trim()
          const availableVaults = Array.isArray(result?.availableVaults)
            ? result.availableVaults
            : []
          const matchingVaults = Array.isArray(result?.matchingVaults) ? result.matchingVaults : []
          const matchingNotes = Array.isArray(result?.matchingNotes) ? result.matchingNotes : []

          if ((actionName === 'update_note' || actionName === 'create_note') && errorCode === 'vault_not_found') {
            return normalizeClarificationPayload({
              kind: 'vault_suggestion',
              prompt: normalizedPrompt,
              choices: buildSimpleClarificationChoices(availableVaults, 'vault')
            })
          }
          if ((actionName === 'update_note' || actionName === 'create_note') && errorCode === 'vault_ambiguous') {
            return normalizeClarificationPayload({
              kind: 'vault_choice',
              prompt: normalizedPrompt,
              choices: buildSimpleClarificationChoices(matchingVaults, 'vault')
            })
          }
          if (actionName === 'update_note' && (errorCode === 'note_candidate' || errorCode === 'note_ambiguous')) {
            return normalizeClarificationPayload({
              kind: errorCode === 'note_candidate' ? 'note_candidate' : 'note_choice',
              prompt: normalizedPrompt,
              choices: buildSimpleClarificationChoices(matchingNotes, 'note')
            })
          }
          return null
        }

        const requestedModelOverride =
          userStore?.isAdmin && typeof modelOverride === 'string' && modelOverride.trim()
            ? modelOverride.trim()
            : null

        if (this.currentStreamCtl && typeof this.currentStreamCtl.cancel === 'function') {
          try {
            this.currentStreamCtl.cancel()
          } catch {
            /* ignore cancellation errors */
          }
        }

        let streamedContent = ''
        let firstDeltaSeen = false
        const ctl = AiChatService.runTurnStreamingCtl({
          files: filesForUpload,
          agentMode: true,
          agentVersion: 'v2',
          contextSnapshot: ctx,
          sessionId: effectiveSessionId,
          turnId: effectiveTurnId,
          latestUserMessage: content,
          clarificationResolution: normalizedClarificationResolution,
          sessionState: this.buildPersistedSessionState(),
          personaId: this.selectedPersonaId || DEFAULT_PERSONA_ID,
          agentOptions: requestedModelOverride
            ? { modelOverride: requestedModelOverride }
            : undefined,
          onStream: (stream) => {
            this.updateAiAvatarStatus({
              phase: typeof stream?.phase === 'string' ? stream.phase : '',
              statusText: typeof stream?.statusText === 'string' ? stream.statusText : ''
            })
            const streamClarification = normalizeClarificationPayload(stream?.clarification)
            if (streamClarification) {
              setAssistantClarification(streamClarification)
            }

            const textChunk =
              (stream && typeof stream.content === 'string' && stream.content) ||
              (stream &&
                typeof stream.text === 'string' &&
                stream.text &&
                typeof stream.statusText !== 'string' &&
                stream.text) ||
              (typeof stream === 'string' ? stream : '')
            if (!textChunk) return

            const cleaned = stripToolPatterns(textChunk)
            if (!cleaned) return

            streamedContent = cleaned
            setAssistantContentThrottled(cleaned)
            if (!firstDeltaSeen) {
              firstDeltaSeen = true
              this.isTyping = false
            }
          }
        })

        this.currentStreamCtl = ctl
        const job = await ctl.promise
        flushStream()
        const resolvedSessionId = String(job?.sessionId || effectiveSessionId || '').trim() || null
        if (resolvedSessionId) {
          this.activeSessionId = resolvedSessionId
          this.persistLastSessionId(resolvedSessionId)
        }

        const reply =
          job?.result?.reply || job?.result?.content || job?.reply || streamedContent || ''
        const finalReply =
          stripToolPatterns(String(reply || streamedContent || '').trim()) ||
          'I finished the requested steps.'
        const finalClarification = normalizeClarificationPayload(
          job?.result?.clarification || job?.stream?.clarification || null
        )

        setAssistantContent(finalReply)
        setAssistantClarification(finalClarification)
        const nextTopicState = buildTopicStateFromJob(job, {
          previousTopicState: this.topicState,
          latestUserText: content
        })
        if (nextTopicState) {
          this.setTopicState(nextTopicState)
        }
        if (resolvedSessionId) {
          this.applySessionSummary({
            sessionId: resolvedSessionId,
            title: content || this.activeSessionSummary?.title || 'New chat',
            personaId: this.selectedPersonaId || DEFAULT_PERSONA_ID,
            sessionState: this.buildPersistedSessionState(),
            updatedAt: new Date().toISOString(),
            lastMessagePreview: finalReply,
            messageCount: Math.max(2, Number(this.activeSessionSummary?.messageCount || 0) + 2)
          })
          await this.syncActiveSessionState()
        }
        const idxLeft = this.messages.findIndex((m) => m.role === 'assistant' && m.placeholder)
        if (idxLeft >= 0) this.messages.splice(idxLeft, 1)
        this.isTyping = false
        this.currentStreamCtl = null
        this.resetAiAvatarState()

        const pending = job?.result?.pendingActions
        const executedPendingFingerprints = new Set()
        if (Array.isArray(pending) && pending.length) {
          for (const action of pending) {
            try {
              const actionFingerprint = JSON.stringify({
                name: String(action?.name || ''),
                args: action?.args || {}
              })
              if (executedPendingFingerprints.has(actionFingerprint)) {
                continue
              }
              executedPendingFingerprints.add(actionFingerprint)
              const executionKey = this.buildPendingActionExecutionKey(action, content)
              if (executionKey && this.hasRecentPendingActionExecution(executionKey)) {
                continue
              }

              if (action.name === 'create_note') {
                const title = String(action?.args?.title || '').trim()
                const content = String(action?.args?.content || '').trim()
                if (!title || !content) {
                  this.pushMessage(
                    'assistant',
                    "I still need the note title and content before I can create that note."
                  )
                  continue
                }
              }

              if (action.name === 'roll_dice') {
                try {
                  const result = await executeTool('roll_dice', action.args || {})
                  if (result?.ok) {
                    if (executionKey) this.markPendingActionExecuted(executionKey)
                    const parts = []
                    parts.push(`Roll ${action.args?.formula || 'dice'}: ${result.total}`)
                    if (action.args?.note) parts.push(`(${action.args.note})`)
                    if (Array.isArray(result.rolls) && result.rolls.length) {
                      parts.push(`[${result.rolls.join(', ')}]`)
                    }
                    this.pushMessage('assistant', parts.join(' '))
                  } else {
                    const errorText = String(result?.error || 'unknown error')
                    const helperText =
                      errorText === 'dice_formula_required'
                        ? 'I need a dice formula, for example: 1d20+5.'
                        : `Roll failed: ${errorText}`
                    this.pushMessage('assistant', helperText)
                  }
                } catch (rollErr) {
                  this.pushMessage(
                    'assistant',
                    `Roll failed: ${rollErr?.message || 'unknown error'}`
                  )
                }
                continue
              }

              if (action.name === 'open_dice_roller') {
                try {
                  await executeTool('open_dice_roller', action.args || {})
                  if (executionKey) this.markPendingActionExecuted(executionKey)
                } catch {
                  /* ignore dice roller open failures */
                }
                continue
              }

              let skipConfirm = action.confirm === false
              if (action.confirm !== false && (action.title || action.message)) {
                const ok = await this.requestConfirm({
                  title: action.title || 'Confirm change',
                  message: action.message || 'Allow this action?'
                })
                if (!ok) continue
                skipConfirm = true
              }

              const exec = await this.runToolCall(action.name, action.args, { skipConfirm })
              if (exec?.result?.ok && action.name === 'update_note') {
                if (executionKey) this.markPendingActionExecuted(executionKey)
                const result = exec.result
                const title = result?.noteTitle || 'Note'
                const vault = result?.vaultName || null
                this.pushMessage(
                  'assistant',
                  vault ? `Updated "${title}" in "${vault}"` : `Updated "${title}"`
                )
              } else if (exec?.result?.ok && action.name === 'create_note') {
                if (executionKey) this.markPendingActionExecuted(executionKey)
                const result = exec.result
                const title = result?.noteTitle || 'Note'
                const vault = result?.vaultName || null
                this.pushMessage(
                  'assistant',
                  vault ? `Created "${title}" in "${vault}"` : `Created "${title}"`
                )
              } else if (exec?.result && exec.result.ok === false) {
                const errorText = formatPendingActionError(action.name, exec.result)
                const useRawErrorText = /^[A-Z]/.test(errorText) || /\?$/.test(errorText)
                const assistantText =
                  action.name === 'create_note'
                    ? useRawErrorText
                      ? errorText
                      : `I couldn't create that note: ${errorText}`
                    : action.name === 'update_note'
                      ? useRawErrorText
                        ? errorText
                        : `I couldn't update that note: ${errorText}`
                      : `That action failed: ${errorText}`
                const clarification = buildPendingActionClarification(
                  action.name,
                  exec.result,
                  assistantText
                )
                if (clarification) {
                  this.messages.push({
                    role: 'assistant',
                    content: assistantText,
                    clarification,
                    stamp: date.formatDate(Date.now(), 'HH:mm'),
                    personaId: this.selectedPersonaId || DEFAULT_PERSONA_ID
                  })
                  continue
                }
                if (action.name === 'create_note') {
                  this.pushMessage(
                    'assistant',
                    useRawErrorText ? errorText : `I couldn't create that note: ${errorText}`
                  )
                } else if (action.name === 'update_note') {
                  this.pushMessage(
                    'assistant',
                    useRawErrorText ? errorText : `I couldn't update that note: ${errorText}`
                  )
                } else {
                  this.pushMessage('assistant', `That action failed: ${errorText}`)
                }
              } else if (exec?.error) {
                const errorText = String(exec.error || 'Action failed')
                if (action.name === 'create_note') {
                  this.pushMessage('assistant', `I couldn't create that note: ${errorText}`)
                } else if (action.name === 'update_note') {
                  this.pushMessage('assistant', `I couldn't update that note: ${errorText}`)
                } else {
                  this.pushMessage('assistant', `That action failed: ${errorText}`)
                }
              } else if (exec?.cancelled) {
                const last = this.messages[this.messages.length - 1]
                const lastWasConfirmAck =
                  last &&
                  last.role === 'assistant' &&
                  typeof last.content === 'string' &&
                  /cancelled/i.test(last.content)
                if (!lastWasConfirmAck) {
                  this.pushMessage('assistant', 'Action cancelled.')
                }
              }
            } catch (error) {
              console.warn('[AiChat] pendingAction failed:', action?.name, error)
            }
          }
        }
      } catch (error) {
        console.error('[AiChat] sendUserMessage failed', error)
        const code = String(error?.code || '')
        const rawMsg = String(error?.message || '')
        const isUnauth = /unauth/i.test(code) || /auth required/i.test(rawMsg)
        const wasCancelled = rawMsg === 'stream_cancelled'
        const errMsg = isUnauth
          ? 'Authentication required. Please sign in to continue.'
          : rawMsg || 'Sorry, I ran into an error.'

        this.isTyping = false
        this.currentStreamCtl = null
        this.resetAiAvatarState()
        if (!wasCancelled) {
          try {
            setAssistantContent(errMsg)
          } catch {
            this.pushMessage('assistant', errMsg)
          }
        } else {
          const idxLeft = this.messages.findIndex((m) => m.role === 'assistant' && m.placeholder)
          if (idxLeft >= 0 && !this.messages[idxLeft]?.content) {
            this.messages.splice(idxLeft, 1)
          }
        }
      } finally {
        this.isSending = false
        this.isTyping = false
        this.attachments = []
        if (!this.currentStreamCtl) {
          this.resetAiAvatarState()
        }
      }
    },

    async runToolCall(name, args, { skipConfirm = false } = {}) {
      const tool = getTool(name)
      if (!tool) {
        return { error: `Tool not available: ${name}` }
      }

      if (tool.kind === 'write' && !skipConfirm) {
        const ok = await this.requestConfirm({
          title: 'Confirm change',
          message: `Allow tool "${name}" to modify data?`
        })
        if (!ok) return { cancelled: true }
      }

      try {
        const timeoutMs = 45000
        const result = await Promise.race([
          executeTool(name, args),
          new Promise((_, reject) => setTimeout(() => reject(new Error('tool_timeout')), timeoutMs))
        ])
        return { result }
      } catch (error) {
        console.error('[AiChat] runToolCall failed', name, error)
        return { error: error?.message || 'tool_error' }
      }
    }
  }
})
